/**
 * NewTaskQuickAction
 * -----------------------------------------------------------------------------
 * Atalho do FAB do chat para criar uma nova Tarefa. Reaproveita o componente
 * `TaskFormDialog` já usado em `TaskList.tsx` — toda a criação acontece
 * server-side via as mesmas RPCs / validações de limites e fases.
 *
 * Regras de visibilidade (espelhadas 1:1 do botão original em TaskList.tsx
 * linhas 883/1229):
 *   `canCreate('tasks_list') && canCreateInAnyPhase`
 *
 * Onde `canCreateInAnyPhase` itera `useTaskPhasePermissions().permissionsMap`
 * procurando ao menos uma fase com `can_create=true` (ou `isPhaseAdmin`).
 *
 * Como o FAB roda fora da rota /tasks (que é protegida por `RequireModule`),
 * adicionamos a checagem de módulo ativo (`tasks_enabled`).
 *
 * Retorna `null` quando módulo desativado OU usuário sem permissão, evitando
 * pílula vazia no FAB.
 *
 * @version 1.1.0
 * RELEASE: 2026-08-22 v1.1.0 - Seletor de tipo (Normal/Retenção/Jurídico) no modal e
 *          visibilidade baseada em useAvailableTaskTypes.
 * RELEASE: 2026-06-03 v1.0.0 - Criação do atalho "Nova Tarefa" no FAB do chat.
 */

import { useMemo, useState } from 'react';
import { ListTodo } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useAvailableTaskTypes } from '@/hooks/useAvailableTaskTypes';
import { useTaskPhasePermissions } from '@/hooks/useTaskPhasePermissions';
import { TaskFormDialog } from '@/components/tasks/modal/TaskFormDialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { createLogger } from '@/lib/logger';

const logger = createLogger('NewTaskQuickAction');

interface NewTaskQuickActionProps {
  /** Contato pré-selecionado ao abrir o dialog (vem do chat). */
  contactId?: string;
}

export function NewTaskQuickAction({ contactId }: NewTaskQuickActionProps) {
  const { currentTenant } = useAuth();
  const { permissionsMap, isAdmin: isPhaseAdmin } = useTaskPhasePermissions();
  // v1.1.0 — tipos que o usuário pode criar (Normal / Retenção / Jurídico)
  const { types: availableTypes } = useAvailableTaskTypes();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  // Espelha exatamente a derivação de TaskList.tsx (linha 414-420).
  const canCreateInAnyPhase = useMemo(() => {
    if (isPhaseAdmin) return true;
    for (const [, perm] of permissionsMap) {
      if (perm.can_create) return true;
    }
    return false;
  }, [isPhaseAdmin, permissionsMap]);

  // Gate de módulo (FAB fora da rota /tasks) + gate de permissão.
  // v1.1.0: basta poder criar em QUALQUER um dos três tipos.
  if (!currentTenant?.tasks_enabled) return null;
  if (availableTypes.length === 0 || !canCreateInAnyPhase) return null;


  return (
    <>
      <div className="rounded-full bg-background shadow-md ring-1 ring-border/50 hover:shadow-lg transition-shadow">
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => {
                  logger.debug('Abrindo dialog de nova tarefa', { contactId });
                  setOpen(true);
                }}
                aria-label="Nova Tarefa"
                className="h-11 w-11 rounded-full flex items-center justify-center text-foreground hover:text-primary transition-colors"
              >
                <ListTodo className="h-5 w-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">Nova Tarefa</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <TaskFormDialog
        open={open}
        onOpenChange={setOpen}
        defaultContactId={contactId}
        /* v1.1.0 — permite escolher o tipo da tarefa no atalho do chat */
        allowTypeSelection
        onSuccess={() => {
          // Mesmas invalidações usadas em TaskList.tsx após criação.
          queryClient.invalidateQueries({ queryKey: ['tasks-paginated'] });
          queryClient.invalidateQueries({ queryKey: ['tasks-usage'] });
        }}
      />
    </>
  );
}
